'use client';

import type {
  IProductSuperGroup,
  IProductSuperGroupEntry
} from '@/services/models/Product';
import { useMemo, type FC } from 'react';
import { isNullish, isNullOrEmpty, maybe } from '@/utils/null-utils';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { kebabCase } from '@/utils/string-utils';
import ProductDetailsVariant from '../ProductDetailsVariant';
import { Tooltip } from '../../core-ui/Tooltip';
import { Icon, IconTypes } from '../../core-ui/Icon';
import { FragmentParser } from '../../utility/FragmentParser';

import S from './styles.module.scss';

export interface ISuperGroupContainerProps {
  /** The super group record to display. */
  superGroup: IProductSuperGroup;
  /** The variant in which this super group is displayed. */
  variant: ProductDetailsVariant;
}

/** Displays a product's super group configuration. */
export const SuperGroupContainer: FC<ISuperGroupContainerProps> = ({
  superGroup,
  variant
}) => {
  const { label, tooltip, entries } = superGroup;

  const pathName = maybe(usePathname());
  const categorySlugs = useMemo(() => {
    const inPDPPage = [
      ProductDetailsVariant.Default,
      ProductDetailsVariant.Exchange
    ].includes(variant);
    if (inPDPPage && !isNullish(pathName)) {
      // If in the PDP page, we can retrieve the category slugs from the current pathname
      // which will be added to the path if the user came from the PLP.
      // Finally, we drop the first and last two segments since the first will
      // be the 'p' and the rest will be the product name and ID.
      return pathName
        .split('/')
        .filter((segment) => !isNullOrEmpty(segment))
        .slice(1, -2);
    }
    // If not in the PDP page, we don't retrieve them from the URL since the
    // path segments won't be necessarily related to any category.
    return [];
  }, [variant, pathName]);
  return (
    <div className={S.superGroupContainer}>
      <div className={S.label}>
        {label}
        {!isNullOrEmpty(tooltip) && (
          <Tooltip
            position="down"
            toggleOnClick
            body={<FragmentParser string={tooltip} />}
          >
            <Icon icon={IconTypes.CircleInfoLight} />
          </Tooltip>
        )}
      </div>
      <div className={S.entryList}>
        {entries.map((entry) => (
          <SuperGroupEntry
            key={entry.id}
            entry={entry}
            categorySlugs={categorySlugs}
          />
        ))}
      </div>
    </div>
  );
};

interface ISuperGroupEntryProps {
  /** A super group entry to display. */
  entry: IProductSuperGroupEntry;

  /** The category slugs to use for the link. */
  categorySlugs: Array<string>;
}

/** Displays a single entry of a product's super group configuration. */
const SuperGroupEntry: FC<ISuperGroupEntryProps> = ({
  entry,
  categorySlugs
}) => {
  // Check to see if the super group entry is selected.
  const isSelected = entry.selected;
  // If there are no category slugs, we don't need to add them to the path.
  // otherwise we add them to the path with a trailing slash.
  const categoryPath =
    categorySlugs.length === 0 ? '' : `${categorySlugs.join('/')}/`;
  if (entry.id) {
    return (
      <Link
        href={`/p/${categoryPath}${kebabCase(entry.label)}/${entry.id}`}
        prefetch={false}
        className={`${S.entry} ${isSelected ? S.selected : ''}`}
      >
        {entry.label}
      </Link>
    );
  }

  return null;
};
